/* eslint-disable react/prop-types */
import React, { useEffect, useState } from "react";
import { connect } from "react-redux";

import { faPlus } from "@fortawesome/pro-regular-svg-icons";

import Button from "../../components/Button";
import { Grid, GridCol } from "../../components/Grid";
// Components
import Icon from "../../components/Icon";
import PageTitle from "../../components/PageTitle";
import Spacer from "../../components/Spacer";
import Table from "../../components/TableLite";
import Typography from "../../components/Text";
import AddEditLinkModal from "./components/AddEditLinkModal";
import DeleteLinkModal from "./components/DeleteLinkModal";
import LinkRow from "./components/LinkRow";

import { useRbacPrimaryRolePermissions } from "../../api/hooks";
// hooks
import { useCompanyLinks } from "../../api/hooks/defs/company-links";
import {
	useAddEditCompanyLink,
	useDeleteCompanyLink,
	useIncrementCompanyLinkScore,
} from "../../api/hooks/defs/company-links";
import useToast from "../../hooks/useToast";

import { getUserIsAdmin } from "../../selectors/app";

// styles
import css from "./CompanyResources.module.scss";
import { CompanyLink } from "./schema";

const columns = [
	{
		id: "name",
		label: "Name",
		isSortable: false,
		getter: (r) => LinkRow(r),
	},
];

const CompanyLinks = ({ userIsAdmin }) => {
	// fetch data
	const { data, isLoading } = useCompanyLinks();
	const [links, setLinks] = useState<CompanyLink[]>([]);
	const toast = useToast();

	useEffect(() => {
		setLinks(data?.links || []);
	}, [data]);

	const [deleteModalShow, setDeleteModalShow] = useState(false);
	const [editModalShow, setEditModalShow] = useState(false);
	const [addLinkModalShow, setAddLinkModalShow] = useState(false);
	const [selectedLink, setSelectedLink] = useState<CompanyLink | null>(null);

	const { mutateAsync: addEditCompanyLinkAsync } = useAddEditCompanyLink();
	const { mutateAsync: incrementCompanyLinkScoreAsync } = useIncrementCompanyLinkScore();
	const { mutateAsync: deleteCompanyLinkAsync } = useDeleteCompanyLink();

	const { data: rbacPermissions, isLoading: isRbacLoading } = useRbacPrimaryRolePermissions("documents");

	const handleSelectEdit = (link) => {
		setSelectedLink(link);
		setEditModalShow(true);
	};

	const handleSelectDelete = (link) => {
		setSelectedLink(link);
		setDeleteModalShow(true);
	};

	const handleDeleteLink = async () => {
		const idToDelete = selectedLink?._id;
		if (idToDelete) {
			try {
				await deleteCompanyLinkAsync(idToDelete);
				const updatedLinks = links.filter((link) => link._id !== idToDelete);
				setLinks(updatedLinks);
				setDeleteModalShow(false);
				setSelectedLink(null);
				toast("Link deleted", {
					type: "success",
					duration: 5000,
					size: "small",
				});
			} catch (e) {
				toast("Error deleting link", {
					type: "error",
					duration: 5000,
					size: "small",
				});
			}
		}
	};

	const handleEditLink = async (data) => {
		const idToDelete = selectedLink?._id;
		if (idToDelete) {
			try {
				await addEditCompanyLinkAsync({ ...data, lid: idToDelete });
				const updatedLinks = links.map((link) => {
					if (link._id === idToDelete) {
						return { ...link, ...data };
					}
					return link;
				});
				setLinks(updatedLinks);
				setEditModalShow(false);
				toast("Link edited", {
					type: "success",
					duration: 5000,
					size: "small",
				});
			} catch (e) {
				toast("Error editing link", {
					type: "error",
					duration: 5000,
					size: "small",
				});
			}
		}
	};

	const handleAddLink = async (data) => {
		try {
			const { link } = await addEditCompanyLinkAsync(data);
			setLinks((prev) => [...prev, { _id: link._id, ...data }]);
			setAddLinkModalShow(false);
			toast("Link added", {
				type: "success",
				duration: 5000,
				size: "small",
			});
		} catch (e) {
			toast("Error adding link", {
				type: "error",
				duration: 5000,
				size: "small",
			});
		}
	};

	const handleEditModalClose = () => {
		setSelectedLink(null);
		setEditModalShow(false);
	};

	const rows =
		links
			?.map((link) => ({
				onSelectEdit: () => handleSelectEdit(link),
				onSelectDelete: () => handleSelectDelete(link),
				incrementCompanyLinkScore: () => incrementCompanyLinkScoreAsync(link._id),
				userIsAdmin: userIsAdmin,
				...link,
			}))
			.sort((a, b) => (a?.title?.toLowerCase() > b?.title?.toLowerCase() ? 1 : -1)) || [];

	if (isLoading || isRbacLoading) {
		return <div />;
	}

	return (
		<>
			<PageTitle title="Company Links" />
			<Grid className={css.Grid} cols={12} gap={3} padding={8}>
				<GridCol span={12}>
					{rbacPermissions?.includes("documents:uploadCompanyDocuments") && (
						<Button
							testid="add-link-button"
							onClick={() => setAddLinkModalShow(true)}
							color="green"
							className={css.linkButton}
						>
							<Icon icon={faPlus} size="small" className={css.uploadIcon} />
							Add Link
						</Button>
					)}
					<Typography size="paragraph-1" weight="semi-bold">
						Company Links{" "}
					</Typography>
					<Spacer height={2} />
					<Table
						theme="vJan23"
						hideHeader={true}
						columns={columns}
						rows={rows}
						tableBodyClass={css.tableBody}
						rowKey="_id"
					/>
				</GridCol>
			</Grid>
			<AddEditLinkModal
				show={addLinkModalShow}
				onClose={() => setAddLinkModalShow(false)}
				onSubmit={handleAddLink}
			/>
			<DeleteLinkModal
				show={deleteModalShow}
				onClose={() => setDeleteModalShow(false)}
				onDelete={handleDeleteLink}
			/>
			<AddEditLinkModal
				show={editModalShow}
				onClose={handleEditModalClose}
				onSubmit={handleEditLink}
				selectedLink={selectedLink}
			/>
		</>
	);
};

const mapStateToProps = (state) => {
	const userIsAdmin = getUserIsAdmin(state);

	return { userIsAdmin: userIsAdmin };
};

export default connect(mapStateToProps, {})(CompanyLinks);
